import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { isLocalStorageEntry, setLocalStorageItemKey } from 'common/utilities/browser/localStorage/localStorage';
import { deepClone } from 'common/utilities/core/object';
import * as I from 'core/api/coreApiClient';
import { newItem } from 'core/redux/actions/localStorage/localStorageActions';
import { pageLoaded, sectionLoaded } from 'core/redux/actions/page/pageActions';
import type { ViewPosition } from 'entityHierarchy/components/EntityHierarchy/EntityHierarchy';
import type { NodePositionWithId } from 'entityHierarchy/components/EntityHierarchyVisualization/EntityHierarchyVisualization';
import type {EntityHierarchyViewId } from 'entityHierarchy/utilities/entityHierarchy';
import { defaultEntityTypeColor } from 'entityHierarchy/utilities/entityHierarchy';

/** Local storage key used to store the positions of nodes after the user moves them */
export const LOCAL_STORAGE_ENTITY_HIERARCHY_VISUALIZATION_POSITIONS = 'ENTITY_HIERARCHY_VISUALIZATION_POSITIONS';
setLocalStorageItemKey(LOCAL_STORAGE_ENTITY_HIERARCHY_VISUALIZATION_POSITIONS);

export enum EntityHierarchyDownload {
  full = 'full',
  partial = 'partial',
  partialFullscreen = 'partialFullscreen', // download modal visualization
}

export enum EntityHierarchyView {
  all = 'all',
  directAscendant = 'directAscendant',
  ascendant = 'ascendant',
  descendant = 'descendant',
}

export enum Orientation {
  horizontal = 'horizontal',
  vertical = 'vertical',
}

export type NodesPositions = NodePositionWithId[];

export type LocalStorageSectionsEntityHierarchyVisualizationPositions = Record<string, EntityHierarchyVisualizationPositions>;

type EntitiesSettings = Record<string, {
    color: string;
    /** Properties shown within nodes: undefined = all, empty array = none  */
    properties?: string[];
  }>;

export interface EntityHierarchyViewSettings {
  orientation: Orientation;
  showIcons: boolean;
  entitiesSettings: EntitiesSettings;
  edges: 'all' | 'simplified';
}

export interface EntityHierarchyVisualizationData {
  nodesPositions?: NodesPositions;
  viewPosition?: ViewPosition;
  viewSettings?: EntityHierarchyViewSettings;
}

/** To be renamed to EntityHierarchyVisualizationsData. It started with positions but now contains more with settings. */
export type EntityHierarchyVisualizationPositions = Record<EntityHierarchyViewId, EntityHierarchyVisualizationData>;

export interface ChangeVisualizationPositionPayloadDate {
  id: string;
  nodesPositions: NodesPositions;
  viewPosition: ViewPosition;
  canUndo: boolean;
  canRedo: boolean;
}

type ChangePositionsAction = 'clear' | 'undo' | 'redo' | undefined;

export interface EntityHierarchyState {
  download?: EntityHierarchyDownload;
  openFullscreenModal?: boolean;
  openViewSettingsModal?: boolean;
  view: EntityHierarchyView;
  clearScale?: boolean;
  processChangePositionsAction?: ChangePositionsAction;
  visualizationPositions: EntityHierarchyVisualizationPositions;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Actions Payloads
 */
interface ScopedActionPayload {
  /** Section ID */
  id: string;
  data?: unknown;
}

interface DownloadActionPayload extends ScopedActionPayload {
  data?: EntityHierarchyDownload;
}

interface ToggleModalActionPayload extends ScopedActionPayload {
  data: boolean;
}

interface ToggleViewSettingsModalActionPayload extends ScopedActionPayload {
  data: boolean;
}

interface ChangeViewActionPayload extends ScopedActionPayload {
  data: EntityHierarchyView;
}

interface ChangeViewSettingsActionPayload extends ScopedActionPayload {
  data: {
    /** View ID */
    id: string;
    viewSettings: EntityHierarchyViewSettings;
    hasOnlyColorsBeenChanged: boolean;
  };
}

interface ChangeClearScaleActionPayload extends ScopedActionPayload {
  data: boolean | undefined;
}

interface ProcessChangePositionsActionPayload extends ScopedActionPayload {
  data: {
    action: ChangePositionsAction;
    canUndo: boolean;
    canRedo: boolean;
  };
}

interface ChangeVisualizationPositionPayload extends ScopedActionPayload {
  data: {
    /** View ID */
    id: string;
    nodesPositions: NodesPositions;
    viewPosition: ViewPosition;
    canUndo: boolean;
    canRedo: boolean;
  };
}

export function getDefaultViewSettings(entityTypes: I.EntityType[]): EntityHierarchyViewSettings {
  return {
    orientation: Orientation.vertical,
    showIcons: false,
    entitiesSettings: entityTypes.reduce<EntitiesSettings>((acc, entityType) => {
      acc[entityType.id] = { color: entityType.color || defaultEntityTypeColor };
      return acc;
    }, {}),
    edges: 'simplified',
  };
}

/**
 * Slice State
 */
export type EntityHierarchiesState = Record<string, EntityHierarchyState>;

export function getEntityHierarchyState(): EntityHierarchyState {
  return {
    view: EntityHierarchyView.all,
    visualizationPositions: {},
    canUndo: false,
    canRedo: false,
  };
}

function getInitialState(): EntityHierarchiesState {
  return {};
}

export const initialState = getInitialState();

/**
 * Update state using the payload ID to scope data
 */
function updateEntityHierarchy(
  state: EntityHierarchiesState, payload: ScopedActionPayload, data: Partial<EntityHierarchyState>,
): EntityHierarchiesState {
  return {
    ...state,
    [payload.id]: (
      state[payload.id]
        ? { ...state[payload.id], ...data }
        : { ...getEntityHierarchyState(), ...data }
    ),
  };
}

const entityHierarchySlice = createSlice({
  name: 'entityHierarchy',
  initialState,
  reducers: {
    download: (state, action: PayloadAction<DownloadActionPayload>) => updateEntityHierarchy(
      state, action.payload, { download: action.payload.data },
    ),
    toggleModal: (state, action: PayloadAction<ToggleModalActionPayload>) => updateEntityHierarchy(
      state, action.payload, { openFullscreenModal: action.payload.data },
    ),
    toggleViewSettingsModal: (
      state, action: PayloadAction<ToggleViewSettingsModalActionPayload>,
    ) => updateEntityHierarchy(
      state, action.payload, { openViewSettingsModal: action.payload.data },
    ),
    changeView: (state, action: PayloadAction<ChangeViewActionPayload>) => updateEntityHierarchy(
      state, action.payload, {
        view: action.payload.data,
        canUndo: false,
        canRedo: false,
      },
    ),
    changeClearScale: (state, action: PayloadAction<ChangeClearScaleActionPayload>) => updateEntityHierarchy(
      state, action.payload, { clearScale: action.payload.data },
    ),
    /** Trigger used to start the process of changing nodes positions via commands, caught in the EntityHierarchy component */
    processChangePositionsAction: (
      state, action: PayloadAction<ProcessChangePositionsActionPayload>,
    ) => updateEntityHierarchy(
      state,
      action.payload,
      {
        processChangePositionsAction: action.payload.data.action,
        canUndo: action.payload.data.canUndo,
        canRedo: action.payload.data.canRedo,
      },
    ),
    changeVisualizationPosition: (
      state, action: PayloadAction<ChangeVisualizationPositionPayload>,
    ) => {
      const newState: EntityHierarchyState = {
        ...deepClone(state[action.payload.id]),
        // Always clear after visualization positions have been changed
        processChangePositionsAction: undefined,
        canUndo: action.payload.data.canUndo,
        canRedo: action.payload.data.canRedo,
      };

      if (action.payload.data.nodesPositions.length === 0) {
        newState.visualizationPositions[action.payload.data.id] = {
          nodesPositions: undefined,
          viewPosition: undefined,
          viewSettings: newState.visualizationPositions[action.payload.data.id]?.viewSettings,
        };
      } else {
        newState.visualizationPositions[action.payload.data.id] = {
          nodesPositions: action.payload.data.nodesPositions,
          viewPosition: action.payload.data.viewPosition,
          viewSettings: newState.visualizationPositions[action.payload.data.id]?.viewSettings,
        };
      }

      return updateEntityHierarchy(state, action.payload, newState);
    },
    changeViewSettings: (
      state, action: PayloadAction<ChangeViewSettingsActionPayload>,
    ) => {
      const newState: EntityHierarchyState = {
        ...deepClone(state[action.payload.id]),
      };

      newState.visualizationPositions[action.payload.data.id] = {
        ...newState.visualizationPositions[action.payload.data.id],
        viewSettings: action.payload.data.viewSettings,
      };

      if (!action.payload.data.hasOnlyColorsBeenChanged) {
        newState.visualizationPositions[action.payload.data.id].nodesPositions = undefined;
        newState.visualizationPositions[action.payload.data.id].viewPosition = undefined;
      }

      return updateEntityHierarchy(state, action.payload, newState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(pageLoaded, (state, action): EntityHierarchiesState => {
      const newState: EntityHierarchiesState = { ...state };
      const section = action.payload.page?.sections.find((s) => s.type === I.SectionTypeConstants.EntityHierarchy);

      if (section && section.expanded && !section.fetchSectionCommand) {
        if (!newState[section.id]) {
          newState[section.id] = getEntityHierarchyState();
        }
      }

      return newState;
    });

    builder.addCase(sectionLoaded, (state, action): EntityHierarchiesState => {
      const newState: EntityHierarchiesState = { ...state };
      const { section } = action.payload;

      if (section && section.type === I.SectionTypeConstants.EntityHierarchy) {
        if (!newState[section.id]) {
          newState[section.id] = getEntityHierarchyState();
        }
      }

      return newState;
    });

    builder.addCase(newItem, (state, action): EntityHierarchiesState => {
      const { item } = action.payload;

      if (isLocalStorageEntry<LocalStorageSectionsEntityHierarchyVisualizationPositions>(
        LOCAL_STORAGE_ENTITY_HIERARCHY_VISUALIZATION_POSITIONS, item,
      )) {
        const newState: EntityHierarchiesState = {};

        Object.keys(item.data).forEach((id) => {
          newState[id] = {
            ...getEntityHierarchyState(),
            visualizationPositions: item.data[id],
          };
        });

        return newState;
      }

      return { ...state };
    });
  },
});

export const {
  download, toggleModal, toggleViewSettingsModal, changeView, changeClearScale,
  processChangePositionsAction, changeVisualizationPosition, changeViewSettings,
} = entityHierarchySlice.actions;

export default entityHierarchySlice.reducer;
